import React from "react";
import CampaignImagePicker from "./campaignImagePicker";

interface GlobalComplianceFormProps {}

const baseUrl = process.env.REACT_APP_API_URL || "";

const GlobalComplianceForm: React.FC<GlobalComplianceFormProps> = () => {
  const [subject, setSubject] = React.useState("");
  const [preheader, setPreheader] = React.useState("");
  const [announcementTitle, setAnnouncementTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [actionRequired, setActionRequired] = React.useState("");
  const [footerNotes, setFooterNotes] = React.useState(
    "This message may contain important legal or compliance information. Please review carefully and consult your own counsel as needed."
  );

  // Hero image state
  const [heroImageUrl, setHeroImageUrl] = React.useState("");
  const [heroUploading, setHeroUploading] = React.useState(false);
  const [heroUploadError, setHeroUploadError] = React.useState<string | null>(
    null
  );
  const [isHeroPickerOpen, setIsHeroPickerOpen] = React.useState(false);

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  function resolveImageUrl(url: string): string {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
  }

  async function handleHeroFileChange(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const file = e.target.files?.[0];
    if (!file) return;

    setHeroUploadError(null);
    setHeroUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", "hero");

      const res = await fetch(`${baseUrl}/api/campaigns/images/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Global hero image upload error:", res.status, text);
        throw new Error(`Upload failed: ${res.status}`);
      }

      const json = await res.json();
      const image = json.image || json;
      const storedUrl = image.url || "";
      const publicUrl = image.publicUrl || "";
      const finalUrl = publicUrl || storedUrl;

      if (!finalUrl) {
        throw new Error("Upload succeeded but no URL returned.");
      }

      setHeroImageUrl(finalUrl);
    } catch (err: any) {
      console.error("Failed to upload global hero image:", err);
      setHeroUploadError(err?.message || "Hero image upload failed.");
    } finally {
      setHeroUploading(false);
      e.target.value = "";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }

    setSubmitting(true);
    try {
      // Map form fields into backend shape
      const intro = announcementTitle.trim();
      let mainBody = body.trim();

      if (actionRequired.trim()) {
        const actionBlock = `\n\nAction required:\n${actionRequired.trim()}`;
        mainBody = mainBody ? mainBody + actionBlock : actionBlock.trim();
      }

      const res = await fetch(
        `${baseUrl}/api/campaigns/global-announcement`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // listIds omitted -> backend defaults to [7] (All Contacts)
            subject: subject.trim(),
            preheader: preheader.trim(),
            intro: intro || "",
            mainBody,
            footerNotes: footerNotes.trim(),
            heroImageUrl: heroImageUrl || null,
          }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        console.error("Global compliance campaign error:", res.status, text);
        alert(
          "Failed to save Global Compliance / Announcement draft (check backend logs)."
        );
        throw new Error(`Failed to create campaign: ${res.status}`);
      }

      const json = await res.json();
      const msg = `Global Compliance / Announcement draft created in Listmonk (ID ${json.listmonkCampaignId}, status: ${json.status}).`;
      setSuccess(msg);
      alert("Global Compliance / Announcement draft successfully sent to backend.");
    } catch (err: any) {
      console.error("Global compliance campaign request failed:", err);
      setError(err?.message || "Failed to create global compliance campaign.");
      alert("Error saving global compliance campaign draft (check console).");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* Hero image picker modal */}
      <CampaignImagePicker
        isOpen={isHeroPickerOpen}
        onClose={() => setIsHeroPickerOpen(false)}
        onSelect={(url: string) => setHeroImageUrl(url)}
        title="Choose a hero image for this Global Compliance / Announcement email"
      />

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4"
      >
        {/* Left: form fields */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">
            Global Compliance / Announcement Details
          </h4>

          {/* Guidance box */}
          <div className="text-xs border border-red-200 bg-red-50 rounded-md p-3 space-y-1">
            <p className="font-semibold text-red-800">
              This tab sends to ALL CONTACTS (global list).
            </p>
            <ul className="list-disc list-inside text-red-900 space-y-0.5">
              <li>Use for required notices, policy updates, or critical advisories.</li>
              <li>No sales or promotional language here.</li>
              <li>Keep the subject clear and specific.</li>
            </ul>
            <p className="mt-1">
              When in doubt, treat this as a legal / compliance notice and keep it factual.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          {success && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              {success}
            </div>
          )}

          {/* Hero image controls */}
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2">
            <p className="text-xs font-semibold text-gray-700">
              Optional hero image
            </p>
            <p className="text-[11px] text-gray-500 mb-1">
              Use a neutral farm / brand image that doesn’t distract from the announcement.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Upload new hero image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="text-xs"
                  onChange={handleHeroFileChange}
                  disabled={heroUploading}
                />
                {heroUploading && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Uploading hero image…
                  </p>
                )}
                {heroUploadError && (
                  <p className="text-[11px] text-red-600 mt-1">
                    {heroUploadError}
                  </p>
                )}
              </div>

              <div className="sm:ml-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Or choose existing
                </label>
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                  onClick={() => setIsHeroPickerOpen(true)}
                >
                  Pick from hero library
                </button>
              </div>
            </div>

            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Hero image URL (optional, read-only)
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-[11px] bg-gray-100"
                value={heroImageUrl}
                readOnly
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Important update from Ridgetop Botanicals"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Preheader</label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              placeholder="Please review this compliance / policy announcement."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Announcement title
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              value={announcementTitle}
              onChange={(e) => setAnnouncementTitle(e.target.value)}
              placeholder="Example: Product advisory, privacy policy update, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Main body (what’s changing)
            </label>
            <textarea
              className="w-full border rounded px-2 py-1 text-sm h-32"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Explain the change, why it matters, and any timelines."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Action required (if any)
            </label>
            <textarea
              className="w-full border rounded px-2 py-1 text-sm h-20"
              value={actionRequired}
              onChange={(e) => setActionRequired(e.target.value)}
              placeholder="Describe what the recipient must or should do, or leave blank if informational only."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Footer notes / disclaimer
            </label>
            <textarea
              className="w-full border rounded px-2 py-1 text-xs h-20"
              value={footerNotes}
              onChange={(e) => setFooterNotes(e.target.value)}
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
              disabled={submitting}
            >
              {submitting
                ? "Creating Global draft…"
                : "Save Global Draft in Listmonk"}
            </button>
          </div>
        </div>

        {/* Right: preview */}
        <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
          <h4 className="text-lg font-semibold">Email Preview (simplified)</h4>

          <div className="text-sm">
            <div>
              <span className="font-semibold">Subject:</span>{" "}
              {subject || "(no subject yet)"}
            </div>
            <div className="text-gray-500">
              <span className="font-semibold">Preheader:</span>{" "}
              {preheader || "(no preheader yet)"}
            </div>
          </div>

          <hr />

          {heroImageUrl && (
            <div className="mb-2">
              <p className="text-xs text-gray-500 mb-1">Hero image preview</p>
              <img
                src={resolveImageUrl(heroImageUrl)}
                alt="Hero preview"
                className="w-full max-h-40 object-cover rounded border border-gray-200"
              />
            </div>
          )}

          <div className="text-sm space-y-2">
            <p className="text-xs text-gray-500">
              (Greeting will be inserted by the Ridgetop base email template and sent to the All Contacts list.)
            </p>

            {announcementTitle && (
              <h5 className="mt-2 font-semibold">{announcementTitle}</h5>
            )}

            <p>{body || "(announcement body will go here…)"}</p>

            {actionRequired && (
              <p className="mt-3">
                <span className="font-semibold">Action required: </span>
                {actionRequired}
              </p>
            )}

            <p className="mt-4 text-[11px] text-gray-500">{footerNotes}</p>
          </div>
        </div>
      </form>
    </>
  );
};

export default GlobalComplianceForm;
